import { useEffect, useState } from "react";
import { api, isAuthed } from "./api";
import type { Lesson, LessonState, Track } from "../data/mock";

/* Live-data hooks over the lessons engine + org endpoints. Each returns null
   until loaded (or when signed out), so callers fall back to seed data. */

// ---- Lessons (POST /lessons/overview) ----------------------------------------

type OverviewStatus = "retired" | "in_progress" | "available" | "locked";
type EngineType = "tools" | "skills" | "mindset";
type OverviewItem = {
  lessonId: string;
  type: EngineType;
  title: string;
  objective: string;
  status: OverviewStatus;
  fadeLevel: "guided" | "prompted" | "independent" | "teach" | null;
  durationMinutes: number | null;
  asksTimeSaved: boolean;
};

const STATE_MAP: Record<OverviewStatus, LessonState> = {
  retired: "mastered",
  in_progress: "active",
  available: "ready",
  locked: "locked",
};
// 4-level fade ladder (L4 = teach-back). Progress climbs toward 100 = mastered.
const FADE_PCT: Record<string, number> = { guided: 22, prompted: 48, independent: 72, teach: 90 };
const FADE_LABEL: Record<string, string> = { guided: "Guided", prompted: "Prompted", independent: "Independent", teach: "Teach-back" };
const GROUP_LABEL: Record<EngineType, string> = { tools: "Tools", skills: "Skills", mindset: "Mindset" };
const GROUP_ICON: Record<EngineType, string> = { tools: "wrench", skills: "brain", mindset: "compass" };
const GROUP_ORDER: EngineType[] = ["tools", "skills", "mindset"];

function toTracks(items: OverviewItem[]): Track[] {
  return GROUP_ORDER.flatMap((type) => {
    const inGroup = items.filter((it) => it.type === type);
    if (!inGroup.length) return [];
    const lessons: Lesson[] = inGroup.map((it) => {
      const state = STATE_MAP[it.status];
      const pct = state === "mastered" ? 100 : state === "active" ? FADE_PCT[it.fadeLevel ?? "guided"] : 0;
      return {
        name: it.title,
        state,
        pct,
        dur: it.durationMinutes ? `${it.durationMinutes} min` : "",
        level: it.fadeLevel ? FADE_LABEL[it.fadeLevel] ?? it.fadeLevel : "Core",
        desc: it.objective,
      };
    });
    return [{ group: GROUP_LABEL[type], icon: GROUP_ICON[type], lessons }];
  });
}

export type LessonsData = { tracks: Track[]; currentLessonId: string | null };

export function useLessons(): LessonsData | null {
  const [data, setData] = useState<LessonsData | null>(null);
  useEffect(() => {
    if (!isAuthed()) return;
    let alive = true;
    api<{ items: OverviewItem[]; currentLessonId: string | null }>("/lessons/overview", {
      method: "POST",
      body: "{}",
    })
      .then((d) => alive && setData({ tracks: toTracks(d.items), currentLessonId: d.currentLessonId }))
      .catch(() => alive && setData(null));
    return () => {
      alive = false;
    };
  }, []);
  return data;
}

// ---- Billing detail (GET /billing/info) --------------------------------------

export type BillingPlan = { id: string; name: string; amount: number | null; interval: string | null; display: string | null };
export type BillingInvoice = { id: string; status: string; total: number; currency: string; createdAt: number; url: string | null };
export type PaymentMethod = { brand: string; last4: string; exp: string | null };
export type BillingInfo = { plans: BillingPlan[]; invoices: BillingInvoice[]; paymentMethod: PaymentMethod | null; adminOnly: boolean };

/** Plan prices + invoices + payment method from Autumn (null until loaded). */
export function useBillingInfo(): BillingInfo | null {
  const [info, setInfo] = useState<BillingInfo | null>(null);
  useEffect(() => {
    if (!isAuthed()) return;
    let alive = true;
    api<BillingInfo>("/billing/info")
      .then((d) => alive && setInfo(d))
      .catch(() => alive && setInfo(null));
    return () => {
      alive = false;
    };
  }, []);
  return info;
}

// ---- Recommendations (GET /recommendations) ----------------------------------

export type Recommendation = { lessonId: string; title: string; type: string; durationMinutes: number | null; reason: string };

/** The engine's next-lesson recommendations with rationale (null until loaded). */
export function useRecommendations(): Recommendation[] | null {
  const [recs, setRecs] = useState<Recommendation[] | null>(null);
  useEffect(() => {
    if (!isAuthed()) return;
    let alive = true;
    api<{ recommendations: Recommendation[] }>("/recommendations")
      .then((d) => alive && setRecs(d.recommendations))
      .catch(() => alive && setRecs(null));
    return () => {
      alive = false;
    };
  }, []);
  return recs;
}

// ---- Engagement: streak + achievements (GET /stats) --------------------------

export type WeekDot = { label: string; on: boolean; today: boolean };
export type AchievementRow = { id: string; t: string; ic: string; done: boolean; prog?: number; goal?: number };
export type TimeSaved = { hours: number; asks: number; spark: number[]; fromReports?: boolean; skills?: number };
export type Stats = {
  streak: number;
  longestStreak: number;
  activeDays: number;
  week: WeekDot[];
  mastered: number;
  inProgress: number;
  totalLessons: number;
  achievements: AchievementRow[];
  timeSaved?: TimeSaved;
};

export function useStats(): Stats | null {
  const [data, setData] = useState<Stats | null>(null);
  useEffect(() => {
    if (!isAuthed()) return;
    let alive = true;
    api<Stats>("/stats")
      .then((d) => alive && setData(d))
      .catch(() => alive && setData(null));
    return () => {
      alive = false;
    };
  }, []);
  return data;
}

// ---- History (GET /history) --------------------------------------------------

const relTime = (ts: number): string => {
  const diff = Date.now() - ts;
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d === 1) return "Yesterday";
  return `${d} days ago`;
};

export type HistoryItem = {
  id: string; t: string; ago: string; mode: string; ic: string;
  userInput: string; assistantOutput: string; model: string; credits: number;
  hadScreenshot: boolean; inputTokens: number; outputTokens: number;
};

type HistoryRow = {
  id: string; title: string; ts: number; model: string; mode: string;
  userInput: string; assistantOutput: string; hadScreenshot: boolean;
  inputTokens: number; outputTokens: number; credits: number;
};

export function useHistory(): HistoryItem[] | null {
  const [items, setItems] = useState<HistoryItem[] | null>(null);
  useEffect(() => {
    if (!isAuthed()) return;
    let alive = true;
    api<{ items: HistoryRow[] }>("/history")
      .then((d) =>
        alive &&
        setItems(d.items.map((it) => ({
          id: it.id,
          t: it.title,
          ago: relTime(it.ts),
          mode: it.mode === "coach" ? "Coach" : "Helper",
          ic: it.mode === "coach" ? "graduation-cap" : "message-square",
          userInput: it.userInput,
          assistantOutput: it.assistantOutput,
          model: it.model,
          credits: it.credits,
          hadScreenshot: it.hadScreenshot,
          inputTokens: it.inputTokens,
          outputTokens: it.outputTokens,
        })))
      )
      .catch(() => alive && setItems(null));
    return () => {
      alive = false;
    };
  }, []);
  return items;
}

// ---- Saved answers & snippets (GET/POST/DELETE /saved) -----------------------

export type SavedItem = { id: string; title: string; body: string; ts: number };

export function useSaved(): {
  items: SavedItem[] | null;
  save: (turnId: string, title?: string) => Promise<void>;
  remove: (id: string) => void;
  reload: () => void;
} {
  const [items, setItems] = useState<SavedItem[] | null>(null);
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    if (!isAuthed()) return;
    let alive = true;
    api<{ items: SavedItem[] }>("/saved")
      .then((d) => alive && setItems(d.items))
      .catch(() => alive && setItems(null));
    return () => {
      alive = false;
    };
  }, [nonce]);
  const reload = () => setNonce((n) => n + 1);
  const save = (turnId: string, title?: string) =>
    api("/saved", { method: "POST", body: JSON.stringify({ turnId, title }) }).then(() => reload());
  const remove = (id: string) => {
    setItems((cur) => (cur ? cur.filter((s) => s.id !== id) : cur)); // optimistic
    api("/saved", { method: "DELETE", body: JSON.stringify({ id }) }).catch(() => {});
  };
  return { items, save, remove, reload };
}

// ---- Team (POST /org/invite) -------------------------------------------------

/** Sends a WorkOS invitation to join the caller's org (admin-only server-side). */
export function inviteMember(email: string) {
  return api("/org/invite", { method: "POST", body: JSON.stringify({ email }) });
}

// ---- Org curriculum: tracks an admin can assign + the org's assignments ------

export type OrgTrack = { id: string; name: string; description: string; icon: string; role: string; lessonCount: number };
export type OrgLessonOption = { id: string; title: string; type: string; icon: string; durationMinutes: number | null };

/** The catalog an admin can assign from — tracks (breadth) + single lessons
   (precision). Null until loaded. */
export function useOrgTracks(): { tracks: OrgTrack[]; lessons: OrgLessonOption[] } | null {
  const [data, setData] = useState<{ tracks: OrgTrack[]; lessons: OrgLessonOption[] } | null>(null);
  useEffect(() => {
    if (!isAuthed()) return;
    let alive = true;
    api<{ tracks: OrgTrack[]; lessons?: OrgLessonOption[] }>("/org/tracks")
      .then((d) => alive && setData({ tracks: d.tracks, lessons: d.lessons ?? [] }))
      .catch(() => alive && setData(null));
    return () => {
      alive = false;
    };
  }, []);
  return data;
}

export type OrgAssignment = { id: string; itemType: "track" | "lesson"; itemId: string; name: string; icon: string; lessonCount: number; scope: string; targetId: string; assignedTo: string };

/** The org's current track assignments, reloadable after assign/unassign. */
export function useOrgAssignments(): { assignments: OrgAssignment[] | null; reload: () => void } {
  const [assignments, setAssignments] = useState<OrgAssignment[] | null>(null);
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    if (!isAuthed()) return;
    let alive = true;
    api<{ assignments: OrgAssignment[] }>("/org/assignments")
      .then((d) => alive && setAssignments(d.assignments))
      .catch(() => alive && setAssignments(null));
    return () => {
      alive = false;
    };
  }, [nonce]);
  return { assignments, reload: () => setNonce((n) => n + 1) };
}

/** Assign a track OR lesson to the whole org or specific member(s)
   (admin-only server-side). */
export function assignOrgItem(itemType: "track" | "lesson", itemId: string, scope: "org" | "user", targetIds?: string[]) {
  return api("/org/assign", { method: "POST", body: JSON.stringify({ itemType, itemId, scope, targetIds }) });
}
export function unassignOrgTrack(assignmentId: string) {
  return api("/org/unassign-track", { method: "POST", body: JSON.stringify({ assignmentId }) });
}

/** Org roster with ids (for the per-member assignment picker). */
export function useOrgMemberOptions(): { id: string; email: string }[] | null {
  const [members, setMembers] = useState<{ id: string; email: string }[] | null>(null);
  useEffect(() => {
    if (!isAuthed()) return;
    let alive = true;
    api<{ members: { id: string; email: string }[] }>("/org/members")
      .then((d) => alive && setMembers(d.members.map((m) => ({ id: m.id, email: m.email }))))
      .catch(() => alive && setMembers(null));
    return () => {
      alive = false;
    };
  }, []);
  return members;
}

// ---- The learner's own assigned tracks (GET /my/assignments) -----------------

export type MyAssignment = { trackId: string; name: string; icon: string; lessonCount: number; via: string; lessonIds: string[] };
export type MyAssignments = { mode: "open" | "assigned"; tracks: MyAssignment[] };

export function useMyAssignments(): MyAssignments | null {
  const [data, setData] = useState<MyAssignments | null>(null);
  useEffect(() => {
    if (!isAuthed()) return;
    let alive = true;
    api<MyAssignments>("/my/assignments")
      .then((d) => alive && setData(d))
      .catch(() => alive && setData(null));
    return () => {
      alive = false;
    };
  }, []);
  return data;
}

// ---- Usage detail (GET /usage) -----------------------------------------------

export type UsageReport = {
  used: number;
  turns: number;
  byModel: { model: string; credits: number; pct: number }[];
  byActivity: { label: string; credits: number }[];
  days: { date: string; sonnet: number; opus: number }[];
  stats: { perDay: number; peakDay: number; activeDays: number; lessonsTaken: number; lessonsMastered: number; projected: number };
};

/** The signed-in user's own cycle usage (null until loaded / when signed out). */
export function useUsage(): UsageReport | null {
  const [data, setData] = useState<UsageReport | null>(null);
  useEffect(() => {
    if (!isAuthed()) return;
    let alive = true;
    api<UsageReport>("/usage")
      .then((d) => alive && setData(d))
      .catch(() => alive && setData(null));
    return () => {
      alive = false;
    };
  }, []);
  return data;
}

// ---- Account settings (GET /settings, POST /settings) ------------------------

export type Settings = Record<string, boolean | string>;

/** Loads the user's stored settings (null until loaded / when signed out). */
export function useSettings(): Settings | null {
  const [settings, setSettings] = useState<Settings | null>(null);
  useEffect(() => {
    if (!isAuthed()) return;
    let alive = true;
    api<{ settings: Settings | null }>("/settings")
      .then((d) => alive && setSettings(d.settings ?? {}))
      .catch(() => alive && setSettings(null));
    return () => {
      alive = false;
    };
  }, []);
  return settings;
}

/** Persists the full settings object. */
export function saveSettings(settings: Settings) {
  return api("/settings", { method: "POST", body: JSON.stringify({ settings }) });
}

// ---- Work profile (GET /profile, POST /profile) ------------------------------

export type StoredProfile = {
  role: string;
  responsibilities: string[];
  techStack: string[];
  timeSinks: string[];
  notes: string;
};

const EMPTY_PROFILE: StoredProfile = { role: "", responsibilities: [], techStack: [], timeSinks: [], notes: "" };

/** Loads the caller's stored work profile (null until loaded / when signed out). */
export function useProfile(): StoredProfile | null {
  const [profile, setProfile] = useState<StoredProfile | null>(null);
  useEffect(() => {
    if (!isAuthed()) return;
    let alive = true;
    api<{ profile: StoredProfile | null }>("/profile")
      .then((d) => alive && setProfile(d.profile ?? EMPTY_PROFILE))
      .catch(() => alive && setProfile(null));
    return () => {
      alive = false;
    };
  }, []);
  return profile;
}

/** Persists the work profile. */
export function saveProfile(profile: StoredProfile) {
  return api("/profile", { method: "POST", body: JSON.stringify({ profile }) });
}

// ---- Learner memory (GET /memory, DELETE /memory) ----------------------------

type MemoryRow = { id: string; kind: string; key: string; value: string; confidence: number; updatedAt: number };
export type MemoryItem = { id: string; t: string; ic: string };

const MEMORY_ICON: Record<string, string> = {
  work_context: "briefcase",
  proficiency: "sparkles",
  delegation_frontier: "git-branch",
  goal: "flag",
};

export function useMemories(): { items: MemoryItem[]; forget: (id: string) => void } | null {
  const [rows, setRows] = useState<MemoryRow[] | null>(null);
  useEffect(() => {
    if (!isAuthed()) return;
    let alive = true;
    api<{ memories: MemoryRow[] }>("/memory")
      .then((d) => alive && setRows(d.memories))
      .catch(() => alive && setRows(null));
    return () => {
      alive = false;
    };
  }, []);
  if (rows === null) return null;
  const items: MemoryItem[] = rows.map((r) => ({ id: r.id, t: r.value, ic: MEMORY_ICON[r.kind] ?? "brain-circuit" }));
  const forget = (id: string) => {
    setRows((cur) => (cur ? cur.filter((r) => r.id !== id) : cur)); // optimistic
    api("/memory", { method: "DELETE", body: JSON.stringify({ id }) }).catch(() => {});
  };
  return { items, forget };
}

// ---- Org (GET /org/members + /org/progress) ----------------------------------

export type Member = { name: string; email: string; role: string; initials: string; you?: boolean; progress?: number };
export type OrgData = { members: Member[]; avgProgress: number };

const titleize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const nameFromEmail = (email: string) =>
  (email.split("@")[0] || "").split(/[._-]+/).filter(Boolean).map(titleize).join(" ") || email;
const initialsFrom = (name: string) => {
  const p = name.split(/\s+/).filter(Boolean);
  return ((p[0]?.[0] || "") + (p[1]?.[0] || "")).toUpperCase() || name.slice(0, 2).toUpperCase();
};

export function useOrg(myEmail?: string): OrgData | null {
  const [data, setData] = useState<OrgData | null>(null);
  useEffect(() => {
    if (!isAuthed()) return;
    let alive = true;
    Promise.all([
      api<{ members: { email: string; role: string }[] }>("/org/members"),
      api<{ members: { email: string; mastered: number; inProgress: number }[] }>("/org/progress").catch(() => ({ members: [] })),
    ])
      .then(([roster, progress]) => {
        const byEmail = new Map(progress.members.map((m) => [m.email, m]));
        const members: Member[] = roster.members.map((m) => {
          const name = nameFromEmail(m.email);
          const p = byEmail.get(m.email);
          const total = p ? p.mastered + p.inProgress : 0;
          return {
            name,
            email: m.email,
            role: titleize(m.role),
            initials: initialsFrom(name),
            you: myEmail ? m.email === myEmail : false,
            progress: total > 0 ? Math.round((p!.mastered / total) * 100) : 0,
          };
        });
        const withProg = members.filter((m) => (m.progress ?? 0) > 0);
        const avgProgress = withProg.length
          ? Math.round(withProg.reduce((n, m) => n + (m.progress ?? 0), 0) / withProg.length)
          : 0;
        if (alive) setData({ members, avgProgress });
      })
      .catch(() => alive && setData(null));
    return () => {
      alive = false;
    };
  }, [myEmail]);
  return data;
}

// ---- Notifications (GET /notifications) --------------------------------------

export type Notification = { id: string; type: string; title: string; body: string | null; link: string | null; read: boolean; ts: number };

/** Polls the caller's in-app notifications (bell/inbox). Returns items, the
 *  unread count, a refresh, and a mark-read that updates locally + on the server. */
export function useNotifications(): {
  items: (Notification & { ago: string })[] | null;
  unread: number;
  reload: () => void;
  markRead: (id?: string) => void;
} {
  const [items, setItems] = useState<(Notification & { ago: string })[] | null>(null);
  const [unread, setUnread] = useState(0);
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    if (!isAuthed()) return;
    let alive = true;
    const load = () =>
      api<{ items: Notification[]; unread: number }>("/notifications")
        .then((d) => {
          if (!alive) return;
          setItems(d.items.map((n) => ({ ...n, ago: relTime(n.ts) })));
          setUnread(d.unread);
        })
        .catch(() => alive && setItems([]));
    load();
    // Light polling so broadcasts/nudges surface without a reload.
    const t = setInterval(load, 60_000);
    return () => { alive = false; clearInterval(t); };
  }, [nonce]);
  const markRead = (id?: string) => {
    setItems((cur) => (cur ? cur.map((n) => (!id || n.id === id ? { ...n, read: true } : n)) : cur));
    setUnread((u) => (id ? Math.max(0, u - 1) : 0));
    api("/notifications/read", { method: "POST", body: JSON.stringify(id ? { id } : {}) }).catch(() => {});
  };
  return { items, unread, reload: () => setNonce((n) => n + 1), markRead };
}

// ---- Path + Catalog (GET /path, /catalog; POST /path/add, /path/remove) ------

export type PathLesson = {
  lessonId: string; title: string; type: EngineType;
  status: OverviewStatus; fadeLevel: string | null; durationMinutes: number | null;
  blockedBy?: { lessonId: string; title: string }[];
  source?: string;
};
export type PathTrack = {
  id: string; name: string; icon: string; description: string; source: string;
  lessons: PathLesson[]; mastered: number; total: number;
};
export type PathData = { tracks: PathTrack[]; lessons: PathLesson[] };

export type CatalogTrack = { id: string; name: string; icon: string; description: string; role: string; lessonCount: number; inPath: boolean };
export type CatalogLesson = { id: string; title: string; type: EngineType; objective: string; durationMinutes: number | null; inPath: boolean };
export type CatalogData = { tracks: CatalogTrack[]; lessons: CatalogLesson[] };

/** The unified Curriculum page's data layer: the learner's path + the live
 *  catalog, with add/remove mutations that refresh both. Null until loaded. */
export function useCurriculum(): {
  path: PathData | null;
  catalog: CatalogData | null;
  addItem: (itemType: "track" | "lesson", itemId: string) => Promise<void>;
  removeItem: (itemType: "track" | "lesson", itemId: string) => Promise<void>;
} {
  const [path, setPath] = useState<PathData | null>(null);
  const [catalog, setCatalog] = useState<CatalogData | null>(null);
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    if (!isAuthed()) return;
    let alive = true;
    api<PathData>("/path").then((d) => alive && setPath(d)).catch(() => alive && setPath(null));
    api<CatalogData>("/catalog").then((d) => alive && setCatalog(d)).catch(() => alive && setCatalog(null));
    return () => { alive = false; };
  }, [nonce]);
  const reload = () => setNonce((n) => n + 1);
  const addItem = (itemType: "track" | "lesson", itemId: string) =>
    api("/path/add", { method: "POST", body: JSON.stringify({ itemType, itemId }) }).then(reload);
  const removeItem = (itemType: "track" | "lesson", itemId: string) =>
    api("/path/remove", { method: "POST", body: JSON.stringify({ itemType, itemId }) }).then(reload);
  return { path, catalog, addItem, removeItem };
}

// ---- Conversation threads (GET /threads) — resumable Helper + lessons --------

export type ThreadRow = {
  id: string; mode: string; lessonId: string | null; fadeLevel: string | null;
  title: string; status: string; turns: number; lastMessageAt: number;
};

/** The caller's resumable threads. `retain` is false when "Store conversation
 *  history" is off — in which case threads/resume aren't available. */
export function useThreads(): { threads: (ThreadRow & { ago: string })[] | null; retain: boolean } {
  const [state, setState] = useState<{ threads: (ThreadRow & { ago: string })[] | null; retain: boolean }>({ threads: null, retain: true });
  useEffect(() => {
    if (!isAuthed()) return;
    let alive = true;
    api<{ threads: ThreadRow[]; retain: boolean }>("/threads")
      .then((d) => alive && setState({ threads: d.threads.map((t) => ({ ...t, ago: relTime(t.lastMessageAt) })), retain: d.retain }))
      .catch(() => alive && setState({ threads: [], retain: true }));
    return () => { alive = false; };
  }, []);
  return state;
}
